<template>
  <f7-page :page-content="false" @page:beforeout="stopPolling" class="txn-result-page">
    <f7-navbar :no-shadow="true" :no-hairline="true">
      <f7-nav-left>
        <f7-link
          v-if="state !== 'success'"
          icon-f7="chevron_left"
          @click="goHome"
        />
      </f7-nav-left>
      <f7-nav-title>{{ pageTitle }}</f7-nav-title>
      <f7-nav-right>
        <navbarright />
      </f7-nav-right>
    </f7-navbar>

    <div class="layout_content txn-scroll">
      <!-- ── Verifying ─────────────────────────────────────────────── -->
      <div v-if="state === 'verifying'" class="hero">
        <div class="hero-icon verifying">
          <f7-preloader size="36" />
        </div>
        <h1 class="hero-title">{{ t('txnResult.verifyingTitle') }}</h1>
        <p class="hero-sub">{{ t('txnResult.verifyingSub') }}</p>
        <f7-card class="skeleton-card">
          <f7-card-content>
            <f7-list>
              <f7-list-item v-for="n in 4" :key="n">
                <f7-skeleton-block style="width: 30%; height: 12px" slot="title" />
                <f7-skeleton-block style="width: 40%; height: 12px" slot="after" />
              </f7-list-item>
            </f7-list>
          </f7-card-content>
        </f7-card>
      </div>

      <!-- ── Resolvable states ─────────────────────────────────────── -->
      <template v-else-if="tx || state === 'unverified'">
        <div class="hero">
          <div class="hero-icon" :class="tone">
            <f7-icon :f7="heroIcon" size="42" />
          </div>
          <h1 class="hero-title">{{ pageTitle }}</h1>
          <p class="hero-sub">
            {{ heroSub }}
            <strong v-if="tx" class="text-primary font-bold text-2xl mt-1 display-block">{{ money(tx.amount, tx.currency) }}</strong>
          </p>
          <f7-chip
            v-if="statusChip"
            class="status-chip"
            :text="statusChip.text"
            :color="statusChip.color"
            :icon="statusChip.icon"
          />
        </div>

        <!-- Trust Reward Banner -->
        <div v-if="state === 'success'" class="max-w-md mx-auto my-3 px-4">
          <div class="bg-gradient-to-r from-secondary-container/40 via-surface-container-low to-secondary-container/30 rounded-2xl p-3.5 shadow-xs display-flex align-items-center justify-content-space-between border border-black/5">
            <div class="display-flex align-items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-white display-flex align-items-center justify-content-center text-secondary shadow-xs">
                <f7-icon material="stars" :size="24" />
              </div>
              <div>
                <div class="display-flex align-items-center gap-1">
                  <span class="font-label-md text-on-surface font-semibold">Confiance Avocash</span>
                  <span class="gold-pill bg-secondary-fixed text-on-secondary-fixed px-1.5 py-0.2 rounded-full font-label-sm font-bold">GOLD</span>
                </div>
                <p class="trust-note font-body-sm text-on-surface-variant">+50 Points de Confiance débloqués !</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Pending: live verification controls -->
        <f7-block v-if="state === 'pending'" class="pending-box">
          <div class="pending-note">
            <span class="pulse-dot" />
            {{ polling ? t('txnResult.autoChecking') : t('txnResult.pendingNote') }}
          </div>
          <f7-button fill round large preloader-spinner-size="0" :loading="refreshing" @click="manualRefresh">
            <f7-icon f7="arrow_clockwise" size="16" />
            {{ t('txnResult.refresh') }}
          </f7-button>
        </f7-block>

        <!-- Failure reason -->
        <div v-if="state === 'failed' && failureReason" class="reason-banner red">
          <f7-icon f7="exclamationmark_circle_fill" size="16" />
          <span>{{ failureReason }}</span>
        </div>

        <!-- Unverified fallback (no reference at all) -->
        <div v-if="state === 'unverified'" class="reason-banner orange">
          <f7-icon f7="question_circle_fill" size="16" />
          <span>{{ t('txnResult.unverifiedNote') }}</span>
        </div>

        <!-- Payout approval progress -->
        <div v-if="state === 'approval'" class="reason-banner teal">
          <f7-icon f7="person_crop_circle_badge_checkmark" size="16" />
          <span>{{ approvalText }}</span>
        </div>

        <!-- Digital Receipt Card -->
        <div v-if="tx" class="max-w-md mx-auto my-4 px-4">
          <div class="bg-surface-container-lowest rounded-2xl shadow-sm border border-black/5 overflow-hidden">
            <div class="h-1.5 width-100 bg-gradient-to-r from-primary via-primary-container to-secondary-container"></div>
            
            <div class="p-4 display-flex align-items-center justify-content-space-between border-b border-black/5">
              <div class="display-flex align-items-center gap-2">
                <f7-icon material="receipt_long" :size="22" class="text-primary" />
                <div>
                  <span class="font-headline-sm text-on-surface font-bold text-sm display-block">{{ t('txnResult.details') }}</span>
                  <span v-if="providerLine" class="font-label-sm text-on-surface-variant">{{ providerLine }}</span>
                </div>
              </div>
              <span class="display-inline-flex align-items-center gap-1 px-2.5 py-0.5 rounded-full bg-primary/10 text-primary font-label-sm font-semibold">
                <f7-icon material="verified" :size="12" />
                TLS 256-bit
              </span>
            </div>

            <div class="p-4 space-y-3 font-body-sm">
              <div class="display-flex justify-content-space-between align-items-center text-sm">
                <span class="text-on-surface-variant">{{ t('common.amount') }}</span>
                <span class="font-bold text-on-surface">{{ money(tx.amount, tx.currency) }}</span>
              </div>
              <div v-if="hasFees" class="display-flex justify-content-space-between align-items-center text-sm">
                <span class="text-on-surface-variant">{{ t('txnResult.fees') }}</span>
                <span class="text-on-surface">{{ money(tx.fees, tx.currency) }}</span>
              </div>
              <div v-if="tx.netAmount != null" class="display-flex justify-content-space-between align-items-center text-sm">
                <span class="text-on-surface-variant">{{ t('txnResult.netAmount') }}</span>
                <span class="font-bold text-primary">{{ money(tx.netAmount, tx.currency) }}</span>
              </div>
              <div class="display-flex justify-content-space-between align-items-center text-sm">
                <span class="text-on-surface-variant">{{ t('txnResult.reference') }}</span>
                <span class="font-mono text-xs text-on-surface font-semibold">{{ tx.transactionReference }}</span>
              </div>
              <div v-if="semanticLabel" class="display-flex justify-content-space-between align-items-center text-sm">
                <span class="text-on-surface-variant">{{ t('txnResult.type') }}</span>
                <span class="text-on-surface">{{ semanticLabel }}</span>
              </div>
              <div v-if="payerLine" class="display-flex justify-content-space-between align-items-center text-sm">
                <span class="text-on-surface-variant">{{ t('common.sender') }}</span>
                <span class="text-on-surface font-medium">{{ payerLine }}</span>
              </div>
              <div v-if="beneficiaryLine" class="display-flex justify-content-space-between align-items-center text-sm">
                <span class="text-on-surface-variant">{{ t('common.recepient') }}</span>
                <span class="text-on-surface font-medium">{{ beneficiaryLine }}</span>
              </div>
              <div v-if="txDate" class="display-flex justify-content-space-between align-items-center text-sm">
                <span class="text-on-surface-variant">{{ t('recipienttransactiondetail.transactionDate') }}</span>
                <span class="text-on-surface">{{ txDate }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <f7-block class="actions">
          <f7-button v-if="state === 'success' && tx?.receiptUrl" outline round large @click="openReceipt">
            {{ t('txnResult.viewReceipt') }}
          </f7-button>
          <f7-button
            v-if="(state === 'failed' || state === 'cancelled') && canRetry"
            fill
            round
            large
            :loading="retrying"
            @click="retryPayment"
          >
            {{ t('txnResult.tryAgain') }}
          </f7-button>
          <f7-button v-if="state === 'success'" fill round large @click="goHome">
            {{ t('txnResult.done') }}
          </f7-button>
          <f7-button v-else-if="state !== 'pending' || !polling" fill round large @click="goHome">
            {{ t('txnResult.close') }}
          </f7-button>
        </f7-block>
      </template>

      <!-- ── Not found ─────────────────────────────────────────────── -->
      <div v-else-if="state === 'notfound'" class="hero">
        <div class="hero-icon grey">
          <f7-icon f7="doc_questionmark_fill" size="42" />
        </div>
        <h1 class="hero-title">{{ t('txnResult.notFoundTitle') }}</h1>
        <p class="hero-sub">{{ t('txnResult.notFoundSub', { reference }) }}</p>
        <f7-block class="actions">
          <f7-button fill round large @click="goHome">{{ t('txnResult.close') }}</f7-button>
        </f7-block>
      </div>
    </div>
  </f7-page>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { f7 } from 'framework7-vue';
import navbarright from '@/components/navbarright.vue';
import i18n from '@/js/i18n.js';
import { usePaymentTransactionStore } from '@/js/pinia-stores/paymentTransaction.js';
import { useAuthStore } from '@/js/pinia-stores/auth.js';
import { useTransactionStatus } from '@/js/composables/useTransactionStatus.js';
import { useInAppReview } from '@/js/composables/useInAppReview.js';
import { newTransactionReference } from '@/js/api/payments.js';
import { formatMoney } from '@/js/utils/money.js';
import { formatDateTime } from '@/js/utils/datetime.js';

const t = i18n.global.t;

const props = defineProps({
  f7route: Object,
  f7router: Object,
});

const paymentTxStore = usePaymentTransactionStore();
const authStore = useAuthStore();
const { transactionStatus } = useTransactionStatus();
const { reportSuccessfulTransaction } = useInAppReview();

// ── Local state ────────────────────────────────────────────────────
const STATE = {
  VERIFYING: 'verifying',
  SUCCESS: 'success',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  PENDING: 'pending',
  APPROVAL: 'approval',
  REFUNDED: 'refunded',
  NOT_FOUND: 'notfound',
  UNVERIFIED: 'unverified',
};
const state = ref(STATE.VERIFYING);
const tx = ref(null);
const loadError = ref(null);
const refreshing = ref(false);
const retrying = ref(false);
const polling = ref(false);

const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 12;
let pollCount = 0;
let pollTimer = null;

// ── Reference resolution ───────────────────────────────────────────
/**
 * Priority: explicit route param (payment.vue now embeds it in
 * returnUrl/cancelUrl) -> persisted store from the initiate call.
 * Provider query params cannot be reversed to an internal reference,
 * so without one of these we can only show a neutral unverified screen.
 */
const reference = computed(() => {
  const routeRef = props.f7route?.params?.transactionId;
  if (routeRef && routeRef.length > 1) return decodeURIComponent(routeRef);
  return (
    paymentTxStore.currentRequest?.transactionReference ||
    paymentTxStore.currentResponse?.transactionReference ||
    null
  );
});

/** Provider-specific query hints (Stripe redirect_status, CinetPay/Paydunya ids). */
const providerHint = computed(() => {
  const q = props.f7route?.query || {};
  if (q.redirect_status) {
    return { source: 'stripe', status: String(q.redirect_status).toLowerCase() };
  }
  if (q.transaction_id) return { source: 'cinetpay', id: q.transaction_id };
  if (q.token) return { source: 'paydunya', id: q.token };
  return null;
});

// ── Derived presentation ───────────────────────────────────────────
const TONE_TITLES = {
  [STATE.SUCCESS]: () => t('txnResult.successTitle'),
  [STATE.FAILED]: () => t('txnResult.failedTitle'),
  [STATE.CANCELLED]: () => t('txnResult.cancelledTitle'),
  [STATE.PENDING]: () => t('txnResult.pendingTitle'),
  [STATE.APPROVAL]: () => t('txnResult.approvalTitle'),
  [STATE.REFUNDED]: () => t('txnResult.refundedTitle'),
  [STATE.NOT_FOUND]: () => t('txnResult.notFoundTitle'),
  [STATE.UNVERIFIED]: () => t('txnResult.unverifiedTitle'),
  [STATE.VERIFYING]: () => t('txnResult.verifyingTitle'),
};

const tone = computed(
  () =>
    ({
      [STATE.SUCCESS]: 'green',
      [STATE.FAILED]: 'red',
      [STATE.CANCELLED]: 'grey',
      [STATE.PENDING]: 'orange',
      [STATE.APPROVAL]: 'orange',
      [STATE.REFUNDED]: 'teal',
      [STATE.NOT_FOUND]: 'grey',
      [STATE.UNVERIFIED]: 'orange',
      [STATE.VERIFYING]: 'orange',
    })[state.value] || 'grey'
);

const heroIcon = computed(
  () =>
    ({
      green: 'checkmark_alt_circle_fill',
      red: 'xmark_circle_fill',
      orange: 'clock_arrow_circlepath',
      teal: 'arrow_uturn_left_circle_fill',
      grey: 'minus_circle_fill',
    })[tone.value] || 'minus_circle_fill'
);

const pageTitle = computed(() => (TONE_TITLES[state.value] ? TONE_TITLES[state.value]() : t('common.transactionStatus')));

const SUBS = {
  [STATE.SUCCESS]: () => t('txnResult.successSub'),
  [STATE.FAILED]: () => t('txnResult.failedSub'),
  [STATE.CANCELLED]: () => t('txnResult.cancelledSub'),
  [STATE.PENDING]: () => t('txnResult.pendingSub'),
  [STATE.APPROVAL]: () => t('txnResult.approvalSub'),
  [STATE.REFUNDED]: () => t('txnResult.refundedSub'),
  [STATE.UNVERIFIED]: () => t('txnResult.unverifiedSub'),
};
const heroSub = computed(() => (SUBS[state.value] ? SUBS[state.value]() : ''));

const statusChip = computed(() => {
  if (!tx.value?.status) return null;
  const info = transactionStatus(tx.value.status);
  const label = info?.status || tx.value.status;
  const colorMap = { green: 'green', orange: 'orange', red: 'red', teal: 'teal' };
  return {
    text: label,
    color: colorMap[info?.color] || 'grey',
    icon: chipIcon(info?.color),
  };
});

function chipIcon(color) {
  switch (color) {
    case 'green': return 'checkmark_circle_fill';
    case 'red': return 'xmark_circle_fill';
    case 'orange': return 'clock_fill';
    case 'teal': return 'arrow_2_circlepath_circle_fill';
    default: return 'question_circle';
  }
}

const failureReason = computed(() => tx.value?.failureReason || paymentTxStore.lastError || '');

const hasFees = computed(() => tx.value?.fees != null && Number(tx.value.fees) !== 0);

const semanticLabel = computed(() => {
  const raw =
    tx.value?.metadata?.entityName ||
    tx.value?.metadata?.campaignName ||
    tx.value?.description?.split('—')[0];
  if (raw) return raw;
  // Derive from entityType when backend displayLabel isn't serialized
  const typeLabels = {
    CAMPAIGN: t('txnResult.entityCampaign'),
    CONTRIBUTION: t('txnResult.entityContribution'),
    ORDER: t('txnResult.entityOrder'),
    SHOP_LISTING: t('txnResult.entityListing'),
    AD_CAMPAIGN: t('txnResult.entityAd'),
    WALLET_TOPUP: t('txnResult.entityTopup'),
    GROUP_BUY: t('txnResult.entityGroupBuy'),
    SAVE_TO_BUY: t('txnResult.entitySaveToBuy'),
    DELIVERY: t('txnResult.entityDelivery'),
    SUBSCRIPTION: t('txnResult.entitySubscription'),
  };
  return typeLabels[tx.value?.entityType] || '';
});

function prettyEnum(v) {
  return v
    ? String(v)
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : '';
}

const providerLine = computed(() => {
  const partner = prettyEnum(tx.value?.paymentPartner);
  const method = prettyEnum(tx.value?.paymentMethod);
  if (partner && method && partner !== method) return `${partner} · ${method}`;
  return partner || method || '';
});

const payerLine = computed(() => {
  const p = tx.value || {};
  return p.payerName || p.payerPhoneNumber || p.payerEmail || p.userDisplayName || '';
});

const beneficiaryLine = computed(() => {
  const b = tx.value || {};
  return (
    b.beneficiaryName ||
    b.beneficiaryPhoneNumber ||
    b.beneficiaryEmail ||
    b.beneficiaryWalletId ||
    ''
  );
});

const txDate = computed(() => {
  const d =
    tx.value?.confirmedAt || tx.value?.executionDate || tx.value?.transactionDate || tx.value?.createdAt;
  return d ? formatDateTime(d) : '';
});

const approvalText = computed(() => {
  const a = tx.value?.payoutApproval || {};
  if (a.required != null && a.current != null) {
    return t('txnResult.approvalProgress', { current: a.current, required: a.required });
  }
  return t('txnResult.approvalInfo');
});

// ── Status classification ──────────────────────────────────────────
const SUCCESS_SET = ['SUCCESS', 'SUCCESSFUL', 'SUCCEEDED', 'COMPLETE', 'CONFIRMED', 'SUCCES', 'ACCEPTED'];
const FAILURE_SET = ['FAILED', 'REJECTED', 'INSUFFICIENT_BALANCE', 'TIMED_OUT', 'TIMED_OUT_3DS'];
const CANCELLED_SET = ['CANCELLED', 'TRANSACTION_CANCEL', 'EXPIRED'];
const REFUND_SET = ['REFUNDED', 'PARTIALLY_REFUNDED'];
const APPROVAL_SET = ['PENDING_APPROVAL'];
const PENDING_SET = [
  'INITIATED',
  'PENDING',
  'PROCESSING',
  'INPROGRESS',
  'OPEN',
  'TO_VALIDATE',
  'PENDING_ON_CONFIRMATION',
  'REQUIRES_PAYMENT_METHOD',
  'AUTHORIZED',
];

function classify(response) {
  // Trust the enum; flags like isSuccessful may serialize as `successful`.
  const s = String(response?.status || '').toUpperCase();
  const flagOk = response?.isSuccessful === true || response?.successful === true;
  const flagFail = response?.isFailed === true || response?.failed === true;
  if (SUCCESS_SET.includes(s) || flagOk) return STATE.SUCCESS;
  if (REFUND_SET.includes(s)) return STATE.REFUNDED;
  if (APPROVAL_SET.includes(s)) return STATE.APPROVAL;
  if (FAILURE_SET.includes(s) || flagFail) return STATE.FAILED;
  if (CANCELLED_SET.includes(s)) return STATE.CANCELLED;
  if (PENDING_SET.includes(s) || response?.isPending === true) return STATE.PENDING;
  return STATE.PENDING;
}

// ── Data loading ───────────────────────────────────────────────────
onMounted(async () => {
  if (!authStore.keycloak?.authenticated) {
    props.f7router.navigate('/login');
    return;
  }
  await verify();
});

onUnmounted(stopPolling);

watch(
  state,
  (next, prev) => {
    if (next === STATE.SUCCESS && prev !== STATE.SUCCESS) {
      reportSuccessfulTransaction();
    }
  },
  { flush: 'post' },
);

async function verify() {
  state.value = STATE.VERIFYING;
  stopPolling();
  tx.value = null;
  loadError.value = null;

  if (!reference.value) {
    // No way to identify the transaction — never guess the outcome.
    state.value = providerHintComputedFallback();
    return;
  }

  try {
    const response = await paymentTxStore.getStatus(reference.value);
    if (!response) throw new Error(t('txnResult.notFoundSub', { reference: reference.value }));
    apply(response);
  } catch (err) {
    loadError.value = err;
    const code = err?.response?.status;
    if (code === 404) {
      state.value = STATE.NOT_FOUND;
    } else {
      // Network/server hiccup: fall back to URL tone, clearly marked unverified.
      state.value = providerHintComputedFallback();
    }
  }
}

/**
 * When the API can't confirm anything, derive a cautious provisional
 * tone from the callback path (success/failure) + provider hint —
 * explicitly framed as unverified so users know to check their balance.
 */
function providerHintComputedFallback() {
  const rt = String(props.f7route?.params?.result_type || '').toLowerCase();
  if (rt.startsWith('fail')) return STATE.UNVERIFIED;
  if (providerHint.value?.status === 'failed') return STATE.UNVERIFIED;
  if (rt === 'sucess' || rt === 'success') return STATE.PENDING;
  return STATE.UNVERIFIED;
}

function apply(response) {
  tx.value = response;
  const next = classify(response);
  state.value = next;
  if (next === STATE.PENDING) startPolling();
}

async function startPolling() {
  if (pollTimer) return;
  polling.value = true;
  pollCount = 0;
  scheduleTick();
}

function scheduleTick() {
  pollTimer = setTimeout(pollOnce, POLL_INTERVAL_MS);
}

async function pollOnce() {
  pollCount += 1;
  try {
    const fresh = await paymentTxStore.refreshStatus(reference.value);
    if (fresh) {
      const next = classify(fresh);
      if (next !== STATE.PENDING) {
        tx.value = fresh;
        state.value = next;
        stopPolling();
        return;
      }
      tx.value = fresh;
    }
  } catch (e) {
    // Transient poll errors are non-fatal; keep trying until budget spent.
  }
  if (pollCount >= MAX_POLLS) {
    polling.value = false;
    pollTimer = null;
    return; // remain on pending with manual refresh
  }
  scheduleTick();
}

function stopPolling() {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
  polling.value = false;
}

async function manualRefresh() {
  refreshing.value = true;
  try {
    const fresh = await paymentTxStore.refreshStatus(reference.value);
    if (fresh) apply(fresh);
  } catch (e) {
    /* keep current state; user can retry */
  } finally {
    refreshing.value = false;
  }
}

// ── Retry (failed / cancelled hosted checkout) ─────────────────────
const canRetry = computed(() => {
  const req = paymentTxStore.currentRequest;
  return !!req && req.transactionReference === reference.value;
});

/**
 * Re-attempts the SAME logical payment with a FRESH reference so the
 * backend treats it as a new attempt (the previous row stays FAILED).
 * returnUrl/cancelUrl are regenerated so the next round-trip still
 * lands here with the new reference.
 */
async function retryPayment() {
  const req = paymentTxStore.currentRequest;
  if (!req || retrying.value) return;
  retrying.value = true;
  try {
    const freshRef = newTransactionReference();
    const origin = window.location.origin;
    const clone = {
      ...req,
      transactionReference: freshRef,
      returnUrl: `${origin}/transaction/sucess/${freshRef}`,
      cancelUrl: `${origin}/transaction/failure/${freshRef}`,
    };
    const response = await paymentTxStore.submit(clone);
    const next = classify(response);
    if (response.returnUrl) {
      window.location.assign(response.returnUrl);
      return;
    }
    if (response.providerSessionToken) {
      // Hand back to the unified pay flow for Stripe popup handling.
      props.f7router.navigate(`/pay/${freshRef}`, { reloadCurrent: true });
      return;
    }
    apply(response);
  } catch (err) {
    f7.dialog.alert(err?.message || t('common.paymentFailed'));
  } finally {
    retrying.value = false;
  }
}

// ── Misc actions ───────────────────────────────────────────────────
function openReceipt() {
  const url = tx.value?.receiptUrl;
  if (!url) return;
  window.open(url, '_blank', 'noopener');
}

function goHome() {
  props.f7router.navigate('/', { reloadCurrent: true, ignoreCache: true });
}

function money(amount, currency) {
  return formatMoney(amount, currency);
}
</script>

<style scoped>
.txn-result-page { --f7-navbar-height: 56px; }
.txn-scroll { padding-bottom: 40px; }

.hero { text-align: center; padding: 28px 20px 8px; }
.hero-icon {
  width: 84px; height: 84px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 14px; color: #fff;
}
.hero-icon.green { background: #34c759; box-shadow: 0 8px 24px rgba(52, 199, 89, 0.35); }
.hero-icon.red { background: #ff3b30; box-shadow: 0 8px 24px rgba(255, 59, 48, 0.35); }
.hero-icon.orange { background: #ff9500; box-shadow: 0 8px 24px rgba(255, 149, 0, 0.35); }
.hero-icon.teal { background: #30b0c7; box-shadow: 0 8px 24px rgba(48, 176, 199, 0.35); }
.hero-icon.grey { background: #8e8e93; box-shadow: 0 8px 24px rgba(142, 142, 147, 0.35); }
.hero-icon.verifying { background: var(--f7-color-bg, #f2f2f7); color: var(--f7-theme-color); }

@keyframes hero-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.06); opacity: 0.85; }
}
.hero-icon.orange { animation: hero-pulse 2s ease-in-out infinite; }

.hero-title { font-size: 22px; font-weight: 700; margin: 0 0 6px; }
.hero-sub { font-size: 13.5px; color: var(--f7-on-surface-variant, #666); margin: 0 auto; max-width: 320px; line-height: 1.5; }
.hero-sub strong { display: block; font-size: 24px; color: var(--f7-text-color, #111); margin-top: 6px; }
.status-chip { margin-top: 12px; }

.gold-pill { font-size: 10px; }
.trust-note { font-size: 12px; }

.skeleton-card { max-width: 420px; margin: 18px auto 0; }

.pending-box { max-width: 420px; margin: 10px auto; text-align: center; }
.pending-note { display: flex; align-items: center; justify-content: center; gap: 8px; font-size: 12.5px; color: var(--f7-on-surface-variant, #666); margin-bottom: 10px; }
.pulse-dot { width: 8px; height: 8px; border-radius: 50%; background: #ff9500; animation: hero-pulse 1.4s ease-in-out infinite; }

.reason-banner {
  display: flex; align-items: flex-start; gap: 8px;
  max-width: 420px; margin: 8px auto; padding: 11px 14px;
  border-radius: 12px; font-size: 13px; line-height: 1.45;
}
.reason-banner.red { background: rgba(255, 59, 48, 0.09); color: #c5281e; }
.reason-banner.orange { background: rgba(255, 149, 0, 0.1); color: #a35c00; }
.reason-banner.teal { background: rgba(48, 176, 199, 0.12); color: #14677a; }

.actions { max-width: 420px; margin: 6px auto 0; display: flex; flex-direction: column; gap: 10px; }
</style>
